<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("storefront_home.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card">
                <template slot="header">
                    <h3 class="mb-0">{{ $t("storefront_home.title") }}</h3>
                    <p class="text-sm text-muted mb-0">{{ $t("storefront_home.subtitle") }}</p>
                </template>

                <div v-loading="loading">
                    <el-collapse v-model="activePanels">
                        <!-- ===================== 1. Top bar / Promo bar ===================== -->
                        <el-collapse-item name="bars">
                            <template slot="title">
                                <strong>{{ $t("storefront_home.sec_topbar") }} / {{ $t("storefront_home.sec_promobar") }}</strong>
                            </template>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.sec_topbar") }} — {{ $t("storefront_home.f_text") }}</label>
                                <el-input v-model="sections.topbar.text" :disabled="!isAdmin"></el-input>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.topbar" :disabled="!isAdmin" @click="saveSection('topbar')">
                                    <i class="fa fa-save"></i> {{ $t("storefront_home.save") }}
                                </base-button>
                                <small v-if="metaText('topbar')" class="text-muted d-block mt-1">{{ metaText('topbar') }}</small>
                            </div>

                            <hr />

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.sec_promobar") }} — {{ $t("storefront_home.f_text") }}</label>
                                <el-input v-model="sections.promobar.text" :disabled="!isAdmin"></el-input>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.promobar" :disabled="!isAdmin" @click="saveSection('promobar')">
                                    <i class="fa fa-save"></i> {{ $t("storefront_home.save") }}
                                </base-button>
                                <small v-if="metaText('promobar')" class="text-muted d-block mt-1">{{ metaText('promobar') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 2. Header ===================== -->
                        <el-collapse-item name="header">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_header") }}</strong></template>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_logo") }}</label>
                                <el-input v-model="sections.header.logo" :disabled="!isAdmin"></el-input>
                            </div>

                            <label class="form-control-label">{{ $t("storefront_home.nav_items") }}</label>
                            <div v-for="(item, i) in sections.header.nav" :key="'nav' + i" class="row align-items-end mb-2 border-bottom pb-2">
                                <!-- 2026-10-13 §5.1: canonical EN label + optional Thai label -->
                                <div class="col-md-3">
                                    <label class="form-control-label small">{{ $t("storefront_home.f_label_en") }}</label>
                                    <el-input v-model="item.label" :disabled="!isAdmin"></el-input>
                                </div>
                                <div class="col-md-3">
                                    <label class="form-control-label small">{{ $t("storefront_home.f_label_th") }}</label>
                                    <el-input v-model="item.label_th" :disabled="!isAdmin"></el-input>
                                </div>
                                <div class="col-md-4">
                                    <label class="form-control-label small">{{ $t("storefront_home.f_link") }}</label>
                                    <el-input v-model="item.link" placeholder="/products" :disabled="!isAdmin"></el-input>
                                </div>
                                <div class="col-md-2 text-right">
                                    <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || i === 0" :title="$t('storefront_home.move_up')" @click="moveItem(sections.header.nav, i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                                    <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || i === sections.header.nav.length - 1" :title="$t('storefront_home.move_down')" @click="moveItem(sections.header.nav, i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                                    <base-button size="sm" type="danger" class="p-1" :disabled="!isAdmin" :title="$t('storefront_home.remove_item')" @click="removeItem(sections.header.nav, i)"><i class="fa fa-trash"></i></base-button>
                                </div>
                            </div>
                            <small class="text-muted d-block mb-2">{{ $t("storefront_home.f_label_th") }}: {{ $t("storefront_home.label_th_hint") }}</small>
                            <base-button size="sm" type="neutral" :disabled="!isAdmin" @click="addItem(sections.header.nav, { label: '', label_th: '', link: '' })"><i class="fa fa-plus"></i> {{ $t("storefront_home.add_item") }}</base-button>

                            <div class="text-right mt-3">
                                <base-button type="primary" size="sm" :loading="!!saving.header" :disabled="!isAdmin" @click="saveSection('header')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('header')" class="text-muted d-block mt-1">{{ metaText('header') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 3. Hero ===================== -->
                        <el-collapse-item name="hero">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_hero") }}</strong></template>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.hero.image_url" @input="sections.hero.image_url = $event" @update:objectKey="sections.hero.image_key = $event"></s3-image-upload>
                                <small class="text-muted d-block">{{ $t("storefront_home.image_stores_key") }}</small>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image_app") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.hero.image_app_url" @input="sections.hero.image_app_url = $event" @update:objectKey="sections.hero.image_app_key = $event"></s3-image-upload>
                                <small class="text-muted d-block">{{ $t("storefront_home.app_image_hint") }}</small>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_title") }}</label>
                                <el-input v-model="sections.hero.title" :disabled="!isAdmin"></el-input>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_subtitle") }}</label>
                                <el-input v-model="sections.hero.subtitle" :disabled="!isAdmin"></el-input>
                            </div>
                            <div class="row">
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_cta_label") }}</label>
                                    <el-input v-model="sections.hero.cta_label" :disabled="!isAdmin"></el-input>
                                </div>
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_cta_link") }}</label>
                                    <el-input v-model="sections.hero.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input>
                                </div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.hero" :disabled="!isAdmin" @click="saveSection('hero')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('hero')" class="text-muted d-block mt-1">{{ metaText('hero') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 4. Campaigns ===================== -->
                        <el-collapse-item name="campaigns">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_campaigns") }}</strong></template>

                            <div v-for="(item, i) in sections.campaigns.items" :key="'camp' + i" class="border rounded p-3 mb-2">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <strong class="text-muted">#{{ i + 1 }}</strong>
                                    <div>
                                        <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || i === 0" @click="moveItem(sections.campaigns.items, i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                                        <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || i === sections.campaigns.items.length - 1" @click="moveItem(sections.campaigns.items, i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                                        <base-button size="sm" type="danger" class="p-1" :disabled="!isAdmin" @click="removeItem(sections.campaigns.items, i)"><i class="fa fa-trash"></i></base-button>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-4 form-group"><label class="form-control-label small">{{ $t("storefront_home.f_title") }}</label><el-input v-model="item.ctitle" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-4 form-group"><label class="form-control-label small">{{ $t("storefront_home.f_amount") }}</label><el-input v-model="item.amount" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-4 form-group"><label class="form-control-label small">{{ $t("storefront_home.f_code") }}</label><el-input v-model="item.code" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-6 form-group"><label class="form-control-label small">{{ $t("storefront_home.f_cta_label") }}</label><el-input v-model="item.cta_label" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-6 form-group"><label class="form-control-label small">{{ $t("storefront_home.f_cta_link") }}</label><el-input v-model="item.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                                </div>
                            </div>
                            <base-button size="sm" type="neutral" :disabled="!isAdmin" @click="addItem(sections.campaigns.items, { ctitle: '', amount: '', code: '', cta_label: '', cta_link: '' })"><i class="fa fa-plus"></i> {{ $t("storefront_home.add_item") }}</base-button>

                            <div class="text-right mt-3">
                                <base-button type="primary" size="sm" :loading="!!saving.campaigns" :disabled="!isAdmin" @click="saveSection('campaigns')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('campaigns')" class="text-muted d-block mt-1">{{ metaText('campaigns') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 5. Bestsellers ===================== -->
                        <el-collapse-item name="bestsellers">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_bestsellers") }}</strong></template>

                            <p class="text-sm text-muted">{{ $t("storefront_home.bestseller_hint") }}</p>
                            <div v-for="(slot, i) in bestsellerSlots" :key="'bs' + i" class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.slot") }} {{ i + 1 }}</label>
                                <el-select
                                    :value="bestsellerSlots[i]"
                                    class="d-block"
                                    filterable
                                    clearable
                                    remote
                                    reserve-keyword
                                    :disabled="!isAdmin"
                                    :placeholder="$t('storefront_home.search_product')"
                                    :remote-method="remoteSearchProducts"
                                    :loading="searchingProducts"
                                    @change="onSlotChange(i, $event)"
                                    @visible-change="onDropdown"
                                >
                                    <el-option v-for="p in productOptions" :key="p.id" :label="optionLabel(p)" :value="p.id">
                                        <span style="display: flex; align-items: center;">
                                            <img v-if="p.image_url" :src="p.image_url" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px; margin-right: 8px;" alt="" />
                                            <span>{{ optionLabel(p) }}</span>
                                        </span>
                                    </el-option>
                                </el-select>
                            </div>

                            <div class="text-right mt-3">
                                <base-button type="primary" size="sm" :loading="!!saving.bestsellers" :disabled="!isAdmin" @click="saveSection('bestsellers')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('bestsellers')" class="text-muted d-block mt-1">{{ metaText('bestsellers') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 6. Campaign banner ===================== -->
                        <el-collapse-item name="banner1">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_banner1") }}</strong></template>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.banner1.image_url" @input="sections.banner1.image_url = $event" @update:objectKey="sections.banner1.image_key = $event"></s3-image-upload>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image_app") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.banner1.image_app_url" @input="sections.banner1.image_app_url = $event" @update:objectKey="sections.banner1.image_app_key = $event"></s3-image-upload>
                                <small class="text-muted d-block">{{ $t("storefront_home.app_image_hint") }}</small>
                            </div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_title") }}</label><el-input v-model="sections.banner1.title" :disabled="!isAdmin"></el-input></div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_subtitle") }}</label><el-input v-model="sections.banner1.subtitle" :disabled="!isAdmin"></el-input></div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_copy") }}</label><el-input v-model="sections.banner1.copy" :disabled="!isAdmin"></el-input></div>
                            <div class="row">
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_label") }}</label><el-input v-model="sections.banner1.cta_label" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_link") }}</label><el-input v-model="sections.banner1.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.banner1" :disabled="!isAdmin" @click="saveSection('banner1')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('banner1')" class="text-muted d-block mt-1">{{ metaText('banner1') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 7. New Arrival ===================== -->
                        <el-collapse-item name="new_arrival">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_new_arrival") }}</strong></template>

                            <div class="row">
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image_left") }}</label>
                                    <s3-image-upload object-type="storefront" :value="sections.new_arrival.image_left_url" @input="sections.new_arrival.image_left_url = $event" @update:objectKey="sections.new_arrival.image_left_key = $event"></s3-image-upload>
                                </div>
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image_product") }}</label>
                                    <s3-image-upload object-type="storefront" :value="sections.new_arrival.image_product_url" @input="sections.new_arrival.image_product_url = $event" @update:objectKey="sections.new_arrival.image_product_key = $event"></s3-image-upload>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image_left_app") }}</label>
                                    <s3-image-upload object-type="storefront" :value="sections.new_arrival.image_left_app_url" @input="sections.new_arrival.image_left_app_url = $event" @update:objectKey="sections.new_arrival.image_left_app_key = $event"></s3-image-upload>
                                </div>
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image_product_app") }}</label>
                                    <s3-image-upload object-type="storefront" :value="sections.new_arrival.image_product_app_url" @input="sections.new_arrival.image_product_app_url = $event" @update:objectKey="sections.new_arrival.image_product_app_key = $event"></s3-image-upload>
                                </div>
                                <div class="col-12">
                                    <small class="text-muted d-block">{{ $t("storefront_home.app_image_hint") }}</small>
                                </div>
                            </div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_eyebrow") }}</label><el-input v-model="sections.new_arrival.eyebrow" :disabled="!isAdmin"></el-input></div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_title") }}</label><el-input v-model="sections.new_arrival.title" :disabled="!isAdmin"></el-input></div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_details") }}</label><el-input v-model="sections.new_arrival.details" type="textarea" :rows="3" :disabled="!isAdmin"></el-input></div>
                            <div class="row">
                                <div class="col-md-4 form-group"><label class="form-control-label">{{ $t("storefront_home.f_price") }}</label><el-input v-model="sections.new_arrival.price" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-4 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_label") }}</label><el-input v-model="sections.new_arrival.cta_label" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-4 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_link") }}</label><el-input v-model="sections.new_arrival.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.new_arrival" :disabled="!isAdmin" @click="saveSection('new_arrival')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('new_arrival')" class="text-muted d-block mt-1">{{ metaText('new_arrival') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 8. New Arrival strip 2 ===================== -->
                        <el-collapse-item name="new_arrival2">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_new_arrival2") }}</strong></template>

                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.new_arrival2.image_url" @input="sections.new_arrival2.image_url = $event" @update:objectKey="sections.new_arrival2.image_key = $event"></s3-image-upload>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("storefront_home.f_image_app") }}</label>
                                <s3-image-upload object-type="storefront" :value="sections.new_arrival2.image_app_url" @input="sections.new_arrival2.image_app_url = $event" @update:objectKey="sections.new_arrival2.image_app_key = $event"></s3-image-upload>
                                <small class="text-muted d-block">{{ $t("storefront_home.app_image_hint") }}</small>
                            </div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_eyebrow") }}</label><el-input v-model="sections.new_arrival2.eyebrow" :disabled="!isAdmin"></el-input></div>
                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_title") }}</label><el-input v-model="sections.new_arrival2.title" :disabled="!isAdmin"></el-input></div>
                            <div class="row">
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_label") }}</label><el-input v-model="sections.new_arrival2.cta_label" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_link") }}</label><el-input v-model="sections.new_arrival2.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.new_arrival2" :disabled="!isAdmin" @click="saveSection('new_arrival2')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('new_arrival2')" class="text-muted d-block mt-1">{{ metaText('new_arrival2') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 9. Essence ===================== -->
                        <el-collapse-item name="essence">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_essence") }}</strong></template>

                            <div v-for="(card, i) in sections.essence.cards" :key="'ess' + i" class="border rounded p-3 mb-2">
                                <strong class="text-muted d-block mb-2">{{ $t("storefront_home.card") }} #{{ i + 1 }}</strong>
                                <div class="form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image") }}</label>
                                    <s3-image-upload object-type="storefront" :value="card.image_url" @input="card.image_url = $event" @update:objectKey="card.image_key = $event"></s3-image-upload>
                                </div>
                                <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_title") }}</label><el-input v-model="card.title" :disabled="!isAdmin"></el-input></div>
                                <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_copy") }}</label><el-input v-model="card.copy" type="textarea" :rows="2" :disabled="!isAdmin"></el-input></div>
                                <div class="form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_theme") }}</label>
                                    <el-select v-model="card.theme" :disabled="!isAdmin" class="d-block">
                                        <el-option value="dark" :label="$t('storefront_home.theme_dark')"></el-option>
                                        <el-option value="light" :label="$t('storefront_home.theme_light')"></el-option>
                                    </el-select>
                                </div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.essence" :disabled="!isAdmin" @click="saveSection('essence')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('essence')" class="text-muted d-block mt-1">{{ metaText('essence') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 10. Collections ===================== -->
                        <el-collapse-item name="collections">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_collections") }}</strong></template>

                            <div v-for="(card, i) in sections.collections.cards" :key="'col' + i" class="border rounded p-3 mb-2">
                                <strong class="text-muted d-block mb-2">{{ $t("storefront_home.card") }} #{{ i + 1 }}</strong>
                                <div class="form-group">
                                    <label class="form-control-label">{{ $t("storefront_home.f_image") }}</label>
                                    <s3-image-upload object-type="storefront" :value="card.image_url" @input="card.image_url = $event" @update:objectKey="card.image_key = $event"></s3-image-upload>
                                </div>
                                <div class="row">
                                    <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_eyebrow") }}</label><el-input v-model="card.eyebrow" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.f_title") }}</label><el-input v-model="card.title" :disabled="!isAdmin"></el-input></div>
                                </div>
                                <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.f_copy") }}</label><el-input v-model="card.copy" type="textarea" :rows="2" :disabled="!isAdmin"></el-input></div>
                                <div class="row">
                                    <div class="col-md-4 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_label") }}</label><el-input v-model="card.cta_label" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-4 form-group"><label class="form-control-label">{{ $t("storefront_home.f_cta_link") }}</label><el-input v-model="card.cta_link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-4 form-group">
                                        <label class="form-control-label">{{ $t("storefront_home.f_theme") }}</label>
                                        <el-select v-model="card.theme" :disabled="!isAdmin" class="d-block">
                                            <el-option value="dark" :label="$t('storefront_home.theme_dark')"></el-option>
                                            <el-option value="light" :label="$t('storefront_home.theme_light')"></el-option>
                                            <el-option value="gift" :label="$t('storefront_home.theme_gift')"></el-option>
                                        </el-select>
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.collections" :disabled="!isAdmin" @click="saveSection('collections')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('collections')" class="text-muted d-block mt-1">{{ metaText('collections') }}</small>
                            </div>
                        </el-collapse-item>

                        <!-- ===================== 11. Footer ===================== -->
                        <el-collapse-item name="footer">
                            <template slot="title"><strong>{{ $t("storefront_home.sec_footer") }}</strong></template>

                            <h4>{{ $t("storefront_home.newsletter") }}</h4>
                            <div class="row">
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.newsletter_title") }}</label><el-input v-model="sections.footer.newsletter_title" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.newsletter_subtitle") }}</label><el-input v-model="sections.footer.newsletter_subtitle" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.newsletter_placeholder") }}</label><el-input v-model="sections.footer.newsletter_placeholder" :disabled="!isAdmin"></el-input></div>
                                <div class="col-md-6 form-group"><label class="form-control-label">{{ $t("storefront_home.newsletter_cta") }}</label><el-input v-model="sections.footer.newsletter_cta" :disabled="!isAdmin"></el-input></div>
                            </div>

                            <div class="form-group"><label class="form-control-label">{{ $t("storefront_home.brand") }}</label><el-input v-model="sections.footer.brand" :disabled="!isAdmin"></el-input></div>

                            <h4>{{ $t("storefront_home.socials") }}</h4>
                            <div v-for="(s, i) in sections.footer.socials" :key="'soc' + i" class="row align-items-end mb-2">
                                <div class="col-md-4">
                                    <label class="form-control-label small">{{ $t("storefront_home.f_platform") }}</label>
                                    <el-select v-model="s.platform" :disabled="!isAdmin" class="d-block">
                                        <el-option v-for="p in socialPlatforms" :key="p" :value="p" :label="p"></el-option>
                                    </el-select>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-control-label small">{{ $t("storefront_home.f_link") }}</label>
                                    <el-input v-model="s.link" placeholder="https://" :disabled="!isAdmin"></el-input>
                                </div>
                                <div class="col-md-2 text-right">
                                    <base-button size="sm" type="danger" class="p-1" :disabled="!isAdmin" @click="removeItem(sections.footer.socials, i)"><i class="fa fa-trash"></i></base-button>
                                </div>
                            </div>
                            <base-button size="sm" type="neutral" :disabled="!isAdmin" @click="addItem(sections.footer.socials, { platform: 'instagram', link: '' })"><i class="fa fa-plus"></i> {{ $t("storefront_home.add_item") }}</base-button>

                            <h4 class="mt-3">{{ $t("storefront_home.col_links") }}</h4>
                            <div v-for="col in footerColumns" :key="col.key" class="border rounded p-3 mb-2">
                                <div class="row">
                                    <div class="col-md-6 form-group">
                                        <label class="form-control-label">{{ $t("storefront_home." + col.i18n) }} — {{ $t("storefront_home.f_title_en") }}</label>
                                        <el-input v-model="sections.footer[col.key].title" :disabled="!isAdmin"></el-input>
                                    </div>
                                    <div class="col-md-6 form-group">
                                        <label class="form-control-label">{{ $t("storefront_home." + col.i18n) }} — {{ $t("storefront_home.f_title_th") }}</label>
                                        <el-input v-model="sections.footer[col.key].title_th" :disabled="!isAdmin"></el-input>
                                    </div>
                                </div>
                                <div v-for="(lnk, li) in sections.footer[col.key].links" :key="col.key + li" class="row align-items-end mb-2">
                                    <div class="col-md-3"><label class="form-control-label small">{{ $t("storefront_home.f_label_en") }}</label><el-input v-model="lnk.label" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-3"><label class="form-control-label small">{{ $t("storefront_home.f_label_th") }}</label><el-input v-model="lnk.label_th" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-4"><label class="form-control-label small">{{ $t("storefront_home.f_link") }}</label><el-input v-model="lnk.link" placeholder="/products" :disabled="!isAdmin"></el-input></div>
                                    <div class="col-md-2 text-right">
                                        <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || li === 0" @click="moveItem(sections.footer[col.key].links, li, -1)"><i class="fa fa-arrow-up"></i></base-button>
                                        <base-button size="sm" type="neutral" class="p-1" :disabled="!isAdmin || li === sections.footer[col.key].links.length - 1" @click="moveItem(sections.footer[col.key].links, li, 1)"><i class="fa fa-arrow-down"></i></base-button>
                                        <base-button size="sm" type="danger" class="p-1" :disabled="!isAdmin" @click="removeItem(sections.footer[col.key].links, li)"><i class="fa fa-trash"></i></base-button>
                                    </div>
                                </div>
                                <small class="text-muted d-block mb-2">{{ $t("storefront_home.f_label_th") }}: {{ $t("storefront_home.label_th_hint") }}</small>
                                <base-button size="sm" type="neutral" :disabled="!isAdmin" @click="addItem(sections.footer[col.key].links, { label: '', label_th: '', link: '' })"><i class="fa fa-plus"></i> {{ $t("storefront_home.add_item") }}</base-button>
                            </div>

                            <div class="form-group mt-3"><label class="form-control-label">{{ $t("storefront_home.copyright") }}</label><el-input v-model="sections.footer.copyright" :disabled="!isAdmin"></el-input></div>

                            <div class="text-right">
                                <base-button type="primary" size="sm" :loading="!!saving.footer" :disabled="!isAdmin" @click="saveSection('footer')"><i class="fa fa-save"></i> {{ $t("storefront_home.save") }}</base-button>
                                <small v-if="metaText('footer')" class="text-muted d-block mt-1">{{ metaText('footer') }}</small>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters } from "vuex";
import { Collapse, CollapseItem, Input, Select, Option } from "element-ui";
import { HomeContentService, AppCatalogProductService } from "@/resource";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";

// The 11 CMS section names (topbar + promobar share one collapse panel but are two rows).
const SECTIONS = [
    "topbar", "promobar", "header", "hero", "campaigns", "bestsellers",
    "banner1", "new_arrival", "new_arrival2", "essence", "collections", "footer"
];

export default {
    name: "StorefrontHomeContent",
    components: {
        S3ImageUpload,
        [Collapse.name]: Collapse,
        [CollapseItem.name]: CollapseItem,
        [Input.name]: Input,
        [Select.name]: Select,
        [Option.name]: Option
    },
    data() {
        return {
            loading: false,
            activePanels: ["bars"],
            sections: this.defaultSections(),
            meta: {},
            saving: {},
            socialPlatforms: ["instagram", "facebook", "tiktok", "pinterest", "line"],
            footerColumns: [
                { key: "col_shop", i18n: "col_shop" },
                { key: "col_about", i18n: "col_about" },
                { key: "col_help", i18n: "col_help" }
            ],
            // Bestseller picker (5 ordered slots)
            bestsellerSlots: [null, null, null, null, null],
            pickedProducts: {}, // id -> { id, code, name, image_url }
            searchResults: [],
            searchingProducts: false
        };
    },
    computed: {
        ...mapGetters({
            isAdmin: "Auth/isAdmin"
        }),
        // Union of the currently-picked products + the latest search results so a saved
        // slot always keeps its label even after a fresh search replaces searchResults.
        productOptions() {
            const byId = {};
            Object.values(this.pickedProducts).forEach((p) => { byId[p.id] = p; });
            this.searchResults.forEach((p) => { if (!byId[p.id]) byId[p.id] = p; });
            return Object.values(byId);
        }
    },
    mounted() {
        this.loadContent();
    },
    methods: {
        // ---- shape defaults (FE owns defaults; a null/missing section falls back here) ----
        defaultSections() {
            return {
                topbar: { text: "" },
                promobar: { text: "" },
                header: { logo: "", nav: [] },
                hero: { title: "", subtitle: "", cta_label: "", cta_link: "", image_key: "", image_url: "", image_app_key: "", image_app_url: "" },
                campaigns: { items: [] },
                bestsellers: { product_ids: [] },
                banner1: { image_key: "", image_url: "", image_app_key: "", image_app_url: "", title: "", subtitle: "", copy: "", cta_label: "", cta_link: "" },
                new_arrival: { eyebrow: "", title: "", details: "", price: "", cta_label: "", cta_link: "", image_left_key: "", image_left_url: "", image_product_key: "", image_product_url: "", image_left_app_key: "", image_left_app_url: "", image_product_app_key: "", image_product_app_url: "" },
                new_arrival2: { eyebrow: "", title: "", cta_label: "", cta_link: "", image_key: "", image_url: "", image_app_key: "", image_app_url: "" },
                essence: { cards: [
                    { image_key: "", image_url: "", title: "", copy: "", theme: "dark" },
                    { image_key: "", image_url: "", title: "", copy: "", theme: "light" }
                ] },
                collections: { cards: [
                    { image_key: "", image_url: "", eyebrow: "", title: "", copy: "", cta_label: "", cta_link: "", theme: "dark" },
                    { image_key: "", image_url: "", eyebrow: "", title: "", copy: "", cta_label: "", cta_link: "", theme: "dark" },
                    { image_key: "", image_url: "", eyebrow: "", title: "", copy: "", cta_label: "", cta_link: "", theme: "light" },
                    { image_key: "", image_url: "", eyebrow: "", title: "", copy: "", cta_label: "", cta_link: "", theme: "gift" }
                ] },
                footer: {
                    newsletter_title: "", newsletter_subtitle: "", newsletter_placeholder: "", newsletter_cta: "",
                    brand: "", socials: [],
                    col_shop: { title: "Shop", title_th: "", links: [] },
                    col_about: { title: "About", title_th: "", links: [] },
                    col_help: { title: "Help", title_th: "", links: [] },
                    copyright: ""
                }
            };
        },
        // label_th / title_th (2026-10-13 §5.1) must exist before the inputs bind,
        // so rows saved before the field existed are reactive and save as "".
        withThaiFields(sections) {
            const fixLinks = (arr) => (Array.isArray(arr) ? arr : []).map((l) => Object.assign({ label_th: "" }, l, {
                label_th: l && typeof l.label_th === "string" ? l.label_th : ""
            }));
            if (sections.header) sections.header = Object.assign({}, sections.header, { nav: fixLinks(sections.header.nav) });
            if (sections.footer) {
                const footer = Object.assign({}, sections.footer);
                ["col_shop", "col_about", "col_help"].forEach((k) => {
                    const col = footer[k] && typeof footer[k] === "object" ? footer[k] : { title: "", links: [] };
                    footer[k] = Object.assign({}, col, {
                        title_th: typeof col.title_th === "string" ? col.title_th : "",
                        links: fixLinks(col.links)
                    });
                });
                sections.footer = footer;
            }
            return sections;
        },
        notifyError(err) {
            this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
        },
        async loadContent() {
            this.loading = true;
            try {
                const res = await HomeContentService.get();
                const body = (res.data && res.data.data) || {};
                const apiSections = body.sections || {};
                this.meta = body.meta || {};
                const defaults = this.defaultSections();
                const merged = {};
                SECTIONS.forEach((name) => {
                    const apiVal = apiSections[name];
                    // A null/missing section -> use the local default shape.
                    merged[name] = apiVal && typeof apiVal === "object"
                        ? Object.assign({}, defaults[name], apiVal)
                        : defaults[name];
                });
                this.sections = this.withThaiFields(merged);
                // Seed bestseller slots + option labels from saved ids.
                const ids = Array.isArray(this.sections.bestsellers.product_ids)
                    ? this.sections.bestsellers.product_ids.filter((v) => typeof v === "number" && v > 0)
                    : [];
                const slots = [null, null, null, null, null];
                ids.slice(0, 5).forEach((id, i) => { slots[i] = id; });
                this.bestsellerSlots = slots;
                await this.seedPickedProducts(ids);
                // element-ui 2.4.11 GOTCHA: remote-method never fires on dropdown open,
                // so pre-populate the option list once with an empty query.
                this.remoteSearchProducts("");
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.loading = false;
            }
        },
        // ---- section save ----
        // Deep-strip every *_url key so only object keys (never CDN URLs) are persisted.
        cleanPayload(v) {
            if (Array.isArray(v)) return v.map((x) => this.cleanPayload(x));
            if (v && typeof v === "object") {
                const o = {};
                Object.keys(v).forEach((k) => {
                    if (k.endsWith("_url")) return;
                    o[k] = this.cleanPayload(v[k]);
                });
                return o;
            }
            return v;
        },
        async saveSection(name) {
            let payload;
            if (name === "bestsellers") {
                payload = {
                    product_ids: this.bestsellerSlots
                        .filter((v) => typeof v === "number" && v > 0)
                        .slice(0, 5)
                };
            } else {
                payload = this.cleanPayload(this.sections[name]);
            }
            this.$set(this.saving, name, true);
            try {
                const res = await HomeContentService.updateSection(name, payload);
                const data = (res.data && res.data.data) || {};
                if (data.updated_at) {
                    this.$set(this.meta, name, Object.assign({}, this.meta[name], { updated_at: data.updated_at }));
                }
                this.$notify({ type: "success", message: this.$t("storefront_home.saved") });
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.$set(this.saving, name, false);
            }
        },
        // ---- list editors ----
        addItem(arr, template) {
            arr.push(JSON.parse(JSON.stringify(template)));
        },
        removeItem(arr, i) {
            arr.splice(i, 1);
        },
        moveItem(arr, i, dir) {
            const j = i + dir;
            if (j < 0 || j >= arr.length) return;
            const tmp = arr[i];
            this.$set(arr, i, arr[j]);
            this.$set(arr, j, tmp);
        },
        // ---- bestseller picker ----
        optionLabel(p) {
            const name = p.name || p.name_th || p.name_en || "";
            return `${p.code || p.id} - ${name}`;
        },
        normalizeProduct(p) {
            return {
                id: p.id,
                code: p.code || "",
                name: p.name || p.name_th || p.name_en || "",
                image_url: p.image_url || ""
            };
        },
        async seedPickedProducts(ids) {
            if (!ids.length) return;
            try {
                const results = await Promise.all(
                    ids.map((id) => AppCatalogProductService.get(id).then(
                        (r) => (r.data && r.data.data) || r.data || null,
                        () => null
                    ))
                );
                results.forEach((p) => {
                    if (p && p.id) this.$set(this.pickedProducts, p.id, this.normalizeProduct(p));
                });
            } catch (e) {
                // Non-fatal: a missing product just shows its raw id until re-picked.
            }
        },
        async remoteSearchProducts(query) {
            this.searchingProducts = true;
            try {
                const params = { page: 1, limit: 30, app_enable: 1 };
                if (query) params.keyword = query;
                const res = await AppCatalogProductService.list(params);
                const list = (res.data && res.data.data) || [];
                this.searchResults = list.map((p) => this.normalizeProduct(p));
            } catch (err) {
                this.searchResults = [];
            } finally {
                this.searchingProducts = false;
            }
        },
        onDropdown(visible) {
            if (visible && !this.searchResults.length && !this.searchingProducts) {
                this.remoteSearchProducts("");
            }
        },
        onSlotChange(i, value) {
            this.$set(this.bestsellerSlots, i, value || null);
            if (value) {
                const p = this.productOptions.find((o) => o.id === value);
                if (p) this.$set(this.pickedProducts, value, p);
            }
        },
        // ---- meta ----
        metaText(name) {
            const m = this.meta[name];
            if (!m || !m.updated_at) return "";
            let time = m.updated_at;
            try {
                time = new Date(m.updated_at).toLocaleString();
            } catch (e) { /* keep raw */ }
            return this.$t("storefront_home.updated_meta", { time, by: m.updated_by || 0 });
        }
    }
};
</script>
